import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import BookingChat from "../components/BookingChat";
import { bookingPagesAPI } from "../services/schedulerAPI";
import { showToast } from "../components/Toast";

/**
 * AIBookingPage - AI-powered booking page with conversational interface
 *
 * This page allows guests to book appointments by chatting with Max and Lucy
 * AI agents in a natural conversational flow.
 *
 * URL: /ai-book/:slug
 */
const AIBookingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [bookingPage, setBookingPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingComplete, setBookingComplete] = useState(false);

  useEffect(() => {
    loadBookingPage();
  }, [slug]);

  const loadBookingPage = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch booking page details
      const response = await bookingPagesAPI.getBySlug(slug);
      setBookingPage(response);
    } catch (err) {
      console.error("Failed to load booking page:", err);
      setError("Booking page not found or unavailable");
      showToast.error("Unable to load booking page");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookingComplete = (details) => {
    setBookingComplete(true);
    showToast.success("Booking confirmed! Check your email for details.");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading AI booking assistant...</p>
        </div>
      </div>
    );
  }

  if (error || !bookingPage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center"
        >
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">❌</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h2>
          <p className="text-gray-600 mb-6">
            {error || "The booking page you're looking for doesn't exist."}
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
          >
            Go to Homepage
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <span>👨‍💼</span>
              <span>Chat with Max & Lucy</span>
              <span>👩‍💼</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
              {bookingPage.title}
            </h1>
            {bookingPage.description && (
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {bookingPage.description}
              </p>
            )}

            {/* Payment Badge */}
            {bookingPage.requires_payment && bookingPage.payment_amount > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full">
                <span className="text-lg">💰</span>
                <span className="font-semibold">${bookingPage.payment_amount}</span>
                {bookingPage.payment_required_timing === "optional" && (
                  <span className="text-sm opacity-75">(optional)</span>
                )}
              </div>
            )}

            {/* Switch to Traditional Booking */}
            <div className="mt-4">
              <button
                onClick={() => navigate(`/book/${slug}`)}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Prefer the traditional booking form? Click here →
              </button>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Sidebar - Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-1 space-y-6"
            >
              {/* Meet Your AI Team */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-indigo-900 mb-4 flex items-center gap-2">
                  <span>👥</span>
                  Meet Your AI Team
                </h3>

                {/* Max Profile */}
                <div className="bg-white rounded-lg p-4 mb-3 border-2 border-indigo-100">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-2xl flex-shrink-0 shadow-md">
                      👨‍💼
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-indigo-900 text-base mb-1">
                        Max
                      </h4>
                      <p className="text-xs text-indigo-600 font-semibold mb-2">
                        Booking Coordinator
                      </p>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        I'll greet you, ask a few questions, check availability, and create your booking.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex justify-center py-1">
                  <span className="text-indigo-400 text-xl">↓</span>
                </div>

                {/* Lucy Profile */}
                <div className="bg-white rounded-lg p-4 border-2 border-purple-100">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl flex-shrink-0 shadow-md">
                      👩‍💼
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-purple-900 text-base mb-1">
                        Lucy
                      </h4>
                      <p className="text-xs text-purple-600 font-semibold mb-2">
                        Confirmation Specialist
                      </p>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        I'll send you a confirmation email with all details and handle payment if needed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* How it Works */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span>💡</span>
                  How It Works
                </h3>
                <ol className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-semibold text-xs">
                      1
                    </span>
                    <span>
                      <strong className="text-indigo-900">👨‍💼 Max greets you:</strong> Max, your AI booking coordinator, will introduce himself and ask you a few questions
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-semibold text-xs">
                      2
                    </span>
                    <span>
                      <strong className="text-indigo-900">💬 Chat naturally:</strong> Just tell Max what you need in plain English — he'll check availability and schedule your appointment
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-semibold text-xs">
                      3
                    </span>
                    <span>
                      <strong className="text-purple-900">👩‍💼 Lucy confirms:</strong> Lucy, your confirmation specialist, will send you an email with all the details and meeting link
                    </span>
                  </li>
                  {bookingPage.requires_payment && (
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-semibold text-xs">
                        4
                      </span>
                      <span>
                        <strong className="text-green-900">💳 Lucy handles payment:</strong>{" "}
                        {bookingPage.payment_required_timing === "optional"
                          ? "Lucy will send an optional payment link"
                          : "Lucy will send a secure payment link to confirm your booking"}
                      </span>
                    </li>
                  )}
                </ol>
              </div>

              {/* AI Features */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center gap-2">
                  <span>🤖</span>
                  AI-Powered Features
                </h3>
                <ul className="space-y-3 text-sm text-purple-800">
                  <li className="flex items-center gap-2">
                    <span className="text-purple-500">✓</span>
                    <span>Natural language understanding</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-purple-500">✓</span>
                    <span>Intelligent scheduling suggestions</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-purple-500">✓</span>
                    <span>Automatic conflict detection</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-purple-500">✓</span>
                    <span>Personalized recommendations</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-purple-500">✓</span>
                    <span>24/7 instant responses</span>
                  </li>
                </ul>
              </div>

              {/* What You Get */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span>✨</span>
                  What You Get
                </h3>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Instant confirmation email</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Calendar invite (.ics file)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Video meeting link</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Automatic reminders</span>
                  </li>
                  {bookingPage.requires_payment && (
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      <span>Secure payment processing</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Trust Badges */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 text-center">
                <p className="text-xs text-gray-500 mb-3">Powered by Claude AI</p>
                <div className="flex items-center justify-center gap-4 text-2xl">
                  <span>🤖</span>
                  <span>🔒</span>
                  <span>⚡</span>
                </div>
                <p className="text-xs text-gray-600 mt-3">
                  Secure • Fast • Intelligent
                </p>
              </div>
            </motion.div>

            {/* Main Chat Area */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-2"
            >
              <BookingChat
                bookingPageSlug={slug}
                onBookingComplete={handleBookingComplete}
              />
            </motion.div>
          </div>

          {/* Success Modal/Overlay */}
          {bookingComplete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setBookingComplete(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">🎉</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  You're All Set!
                </h2>
                <p className="text-gray-600 mb-6">
                  Your booking has been confirmed. We've sent you an email with all the details.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => setBookingComplete(false)}
                    className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => navigate("/")}
                    className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold"
                  >
                    Back to Homepage
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIBookingPage;
